import React, { useState } from "react";
import { Alert, Button, Radio, Space, Table, Tag, Typography } from "antd";
import {
  ACTION_COL,
  AFTER_COL,
  BEFORE_COL,
  BeforeAfterLegend,
} from "./beforeAfter";
import { languagesList } from "../../../../utils/constants";
import { fmtDay } from "../institutionMerge.constants";

const { Text } = Typography;

const STATUS_FR = { active: "actif", inactive: "inactif", archived: "archivé" };
const statusColor = b =>
  b.archived || b.status === "archived"
    ? "default"
    : b.status === "active"
    ? "green"
    : "orange";
const languageLabel = code => {
  const l = languagesList.find(x => x.value === code);
  return l ? l.label : String(code || "").toUpperCase();
};

const ELSEWHERE_SHOWN = 3;
/**
 * « aussi exclu·e ailleurs : A, B, C … et N autres — tout voir » — the exclusions at institutions outside the merge:
 * context only (nothing changes there), so the list starts folded and unfolds on demand.
 */
function Elsewhere({ list }) {
  const [open, setOpen] = useState(false);
  if (!list || !list.length) return null;
  const names = list.map(x => x.name);
  const shown = open ? names : names.slice(0, ELSEWHERE_SHOWN);
  const more = names.length - shown.length;
  return (
    <Text type="secondary" style={{ fontSize: 12 }}>
      aussi exclu·e ailleurs ({names.length}, hors fusion, inchangé) :{" "}
      {shown.join(", ")}
      {more > 0 ? ` … et ${more} autre${more > 1 ? "s" : ""}` : ""}{" "}
      <Button
        type="link"
        size="small"
        style={{ fontSize: 12, padding: 0, height: "auto" }}
        onClick={() => setOpen(o => !o)}
      >
        {open ? "réduire" : "tout voir"}
      </Button>
    </Text>
  );
}

/** « Malévoz - La Forêt (institution qui reçoit) » — how an excluding institution is named in a sentence. */
const excluderName = x =>
  x.isTarget ? `${x.name} (institution qui reçoit)` : x.name;

/**
 * Step 6 — interpreter blacklists. Never migrated automatically: EVERY excluded
 * interpreter gets an explicit keep / drop, the receiving institution's own exclusions
 * included when merging into an existing institution (the resulting institution IS the
 * receiver, so its exclusions are as much a decision as the sources'). The table gives the
 * operator everything needed to judge: who the interpreter is (e-mail, status, languages),
 * which institutions exclude them — the receiver tagged —, where else they are excluded
 * outside the merge (context, untouched), where they actually worked inside the merge and
 * when for the last time, and « Avant → Après » for the decision taken.
 */
export function StepBlacklists({ preview, decisions, onChange, disabled }) {
  const list = preview.blacklists;
  if (!list.length)
    return (
      <Alert
        type="success"
        showIcon
        message="Aucun interprète exclu par l'un des établissements."
      />
    );
  const chosen = decisions.blacklists || {};
  const existing = preview.existingTarget || null;
  const targetName =
    decisions.targetName ||
    (existing && existing.name) ||
    "la nouvelle institution";
  const setAll = v =>
    onChange({
      blacklists: Object.fromEntries(list.map(b => [b.interpreterId, v])),
    });
  const byReceiver = list.filter(b =>
    (b.excludedAt || []).some(x => x.isTarget),
  ).length;

  const columns = [
    {
      title: "Interprète",
      dataIndex: "name",
      width: 180,
      render: (n, b) => (
        <Space direction="vertical" size={2}>
          <Space size={6}>
            <Text strong>{n}</Text>
            <Tag color={statusColor(b)} style={{ whiteSpace: "nowrap" }}>
              {STATUS_FR[b.status] || b.status || "—"}
            </Tag>
          </Space>
          {b.email && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {b.email}
            </Text>
          )}
          {(b.languages || []).length > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              langues : {b.languages.map(languageLabel).join(", ")}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Exclu·e par",
      dataIndex: "excludedAt",
      width: 280,
      ...BEFORE_COL,
      render: (xs, b) => (
        <Space direction="vertical" size={4}>
          <Space wrap size={4}>
            {(xs || []).map(x => (
              <Tag key={x._id} color={x.isTarget ? "orange" : "default"}>
                {x.name}
                {x.isTarget ? " — institution qui reçoit" : ""}
              </Tag>
            ))}
          </Space>
          <Elsewhere list={b.excludedElsewhere} />
        </Space>
      ),
    },
    {
      title: "A travaillé pour",
      dataIndex: "workedAt",
      width: 200,
      ...BEFORE_COL,
      render: xs =>
        xs && xs.length ? (
          <Space direction="vertical" size={4}>
            {xs.map(x => (
              <div key={x._id}>
                <Text>
                  {x.name} <Text strong>{x.n}×</Text>
                </Text>
                {x.isTarget ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {" "}
                    (institution qui reçoit)
                  </Text>
                ) : null}
                {x.last ? (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      dernière le {fmtDay(x.last)}
                    </Text>
                  </div>
                ) : null}
              </div>
            ))}
          </Space>
        ) : (
          <Text type="secondary">jamais dans ces établissements</Text>
        ),
    },
    {
      title: "Avant → Après",
      children: [
        {
          title: "Avant",
          width: 170,
          ...BEFORE_COL,
          render: (_, b) => {
            const names = (b.excludedAt || []).map(excluderName).join(", ");
            return (
              <Text>
                exclu·e chez :{" "}
                {chosen[b.interpreterId] ? (
                  <Text delete type="secondary">
                    {names}
                  </Text>
                ) : (
                  <Text strong>{names}</Text>
                )}
              </Text>
            );
          },
        },
        {
          title: "Après",
          width: 170,
          ...AFTER_COL,
          render: (_, b) => {
            const d = chosen[b.interpreterId];
            if (d === "keep")
              return (
                <Text>
                  exclu·e chez : <Text strong>{targetName}</Text>
                </Text>
              );
            if (d === "drop")
              return (
                <Text>
                  <Text strong>plus exclu·e</Text>{" "}
                  <Text type="secondary">
                    (de nouveau disponible pour {targetName}
                    {(b.excludedElsewhere || []).length
                      ? " ; les exclusions hors fusion restent"
                      : ""}
                    )
                  </Text>
                </Text>
              );
            return (
              <Tag color="red" style={{ whiteSpace: "nowrap" }}>
                à choisir
              </Tag>
            );
          },
        },
      ],
    },
    {
      title: "Décision",
      width: 225,
      ...ACTION_COL,
      render: (_, b) => (
        <Radio.Group
          size="small"
          disabled={disabled}
          value={chosen[b.interpreterId] || null}
          onChange={e =>
            onChange({ blacklists: { [b.interpreterId]: e.target.value } })
          }
        >
          <Radio.Button value="keep">Garder l'exclusion</Radio.Button>
          <Radio.Button value="drop">Retirer</Radio.Button>
        </Radio.Group>
      ),
    },
  ];
  const undecided = list.filter(b => !chosen[b.interpreterId]).length;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message={`${list.length} interprète(s) exclu(s) par au moins un établissement — ${undecided} sans décision.`}
        description={
          <>
            Une exclusion est un jugement sur UN établissement ; elle n'est
            jamais reportée automatiquement. « Garder » l'applique à
            l'institution fusionnée — c'est le choix sûr : un interprète exclu à
            tort se voit et se corrige en un clic, un interprète réadmis en
            silence non.
            {existing && (
              <>
                {" "}
                <Text strong>
                  {byReceiver
                    ? `${byReceiver} interprète(s) sont exclu(s) par ${existing.name}, l'institution qui reçoit : ces exclusions sont décidées ici aussi.`
                    : `${existing.name}, l'institution qui reçoit, n'exclut aucun interprète aujourd'hui.`}
                </Text>{" "}
                « Garder » : l'interprète reste exclu·e de {existing.name} après
                la fusion ; « Retirer » : l'exclusion est supprimée, que ce soit
                un ancien établissement ou {existing.name} qui l'ait prononcée.
                Les exclusions chez des institutions hors fusion ne changent
                pas.
              </>
            )}
          </>
        }
      />
      <Space wrap>
        <Button disabled={disabled} onClick={() => setAll("keep")}>
          Tout garder
        </Button>
        <Button disabled={disabled} danger onClick={() => setAll("drop")}>
          Tout retirer
        </Button>
        <BeforeAfterLegend />
      </Space>
      <Table
        size="small"
        pagination={false}
        rowKey="interpreterId"
        columns={columns}
        dataSource={list}
        bordered
      />
    </Space>
  );
}
